import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { verifyMasterCode } from "../../lib/masterUnlock";
import { CommunityShell, C } from "../../components/CommunityShell";

const LS_CODE = "maldek_master_code_v1";
const TYPES = [
  { id: "doctors_office", label: "Doctor's office / clinic" },
  { id: "community_center", label: "Community center / library" },
  { id: "insurance", label: "Insurance / health plan" },
  { id: "school", label: "School / training program" },
  { id: "other", label: "Other" },
];
const typeLabel = (id) => TYPES.find((t) => t.id === id)?.label || id;
const masterHeaders = () => ({ "X-Master-Code": localStorage.getItem(LS_CODE) || "" });

const btnPrimary = { background: C.ACCENT, color: "#FFFDF8", fontFamily: C.SANS };
const btnDark = { background: C.INK, color: "#FFFDF8", fontFamily: C.SANS };
const btnGhost = { background: C.CARD, color: C.DEEP, border: `1px solid ${C.BORDER}`, fontFamily: C.SANS };
const btnDanger = { background: "#FDF0EC", color: "#A33D2A", border: "1px solid #E8C4B8", fontFamily: C.SANS };
const inputStyle = { background: C.CARD, border: `1px solid ${C.BORDER}`, color: C.INK, fontFamily: C.SANS };

function Gate({ onUnlocked }) {
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const res = await verifyMasterCode(code);
    if (res.ok) onUnlocked();
    else setErr("Code rejected.");
    setBusy(false);
  };
  return (
    <form onSubmit={submit} data-testid="partner-gate" className="rounded-2xl border p-8 max-w-md" style={{ background: C.CARD, borderColor: C.BORDER }}>
      <p className="text-[12px] font-bold uppercase tracking-[0.24em] mb-2" style={{ color: C.ACCENT }}>Admin access</p>
      <h2 className="text-[22px] font-extrabold tracking-tight mb-2" style={{ color: C.INK, fontFamily: C.DISPLAY }}>Master code required.</h2>
      <p className="text-[13px] mb-4" style={{ color: C.SUB }}>Creating facilities, assigning IDs, and deleting data requires the master code.</p>
      <input
        type="password" value={code} onChange={(e) => { setCode(e.target.value); setErr(""); }}
        placeholder="Master code" autoFocus inputMode="numeric"
        data-testid="partner-gate-input"
        className="w-full px-4 py-2.5 rounded-xl text-[15px] tracking-widest mb-3 focus:outline-none"
        style={inputStyle}
      />
      {err && <p className="text-[13px] mb-3" style={{ color: "#A33D2A" }} data-testid="partner-gate-error">{err}</p>}
      <button type="submit" disabled={busy || !code} data-testid="partner-gate-submit"
        className="px-6 py-2.5 rounded-full text-[13px] font-bold disabled:opacity-40 transition-all hover:-translate-y-0.5" style={btnPrimary}>
        {busy ? "Verifying…" : "Unlock →"}
      </button>
    </form>
  );
}

function CopyId({ id, testid }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button" data-testid={testid}
      onClick={() => { navigator.clipboard?.writeText(id); setCopied(true); setTimeout(() => setCopied(false), 1200); }}
      className="px-2 py-0.5 rounded-md text-[11px] font-mono font-bold tracking-wider transition-colors"
      style={{ background: C.PANEL, color: copied ? "#3E7245" : C.DEEP, border: `1px solid ${C.BORDER}` }}
      title="Click to copy"
    >
      {copied ? "Copied!" : id}
    </button>
  );
}

function CohortBlock({ cohort, onChanged }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const addMember = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setBusy(true);
    try {
      await api.post(`/community/partners/cohorts/${cohort.cohort_id}/members`, { name: name.trim() }, { headers: masterHeaders() });
      setName("");
      onChanged();
    } catch (err) { alert(err?.response?.data?.detail || "Couldn't add member."); }
    finally { setBusy(false); }
  };
  const delMember = async (m) => {
    if (!window.confirm(`Delete member "${m.name}" (${m.member_id})? This can't be undone.`)) return;
    await api.delete(`/community/partners/members/${m.member_id}`, { headers: masterHeaders() });
    onChanged();
  };
  const delCohort = async () => {
    if (!window.confirm(`Delete cohort "${cohort.name}" and all its members? This can't be undone.`)) return;
    await api.delete(`/community/partners/cohorts/${cohort.cohort_id}`, { headers: masterHeaders() });
    onChanged();
  };
  return (
    <div className="rounded-xl border p-4" style={{ background: C.BG, borderColor: C.BORDER }} data-testid={`cohort-block-${cohort.cohort_id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-2">
        <h4 className="text-[15px] font-bold" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{cohort.name}</h4>
        <CopyId id={cohort.cohort_id} testid={`cohort-id-${cohort.cohort_id}`} />
        <span className="text-[11px]" style={{ color: C.FAINT }}>{(cohort.members || []).length} member{(cohort.members || []).length === 1 ? "" : "s"}</span>
        <button type="button" onClick={delCohort} data-testid={`cohort-delete-${cohort.cohort_id}`}
          className="ml-auto px-2.5 py-1 rounded-full text-[11px] font-bold transition-all hover:-translate-y-0.5" style={btnDanger}>
          Delete cohort
        </button>
      </div>
      {(cohort.members || []).length > 0 && (
        <ul className="space-y-1.5 mb-3">
          {cohort.members.map((m) => (
            <li key={m.member_id} className="flex items-center gap-2 text-[13px]" style={{ color: C.SUB }} data-testid={`member-row-${m.member_id}`}>
              <span className="font-semibold" style={{ color: C.INK }}>{m.name}</span>
              <CopyId id={m.member_id} testid={`member-id-${m.member_id}`} />
              <button type="button" onClick={() => delMember(m)} data-testid={`member-delete-${m.member_id}`}
                className="ml-auto text-[11px] font-bold hover:underline" style={{ color: "#A33D2A" }}>
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
      <form onSubmit={addMember} className="flex gap-2">
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Person's name"
          data-testid={`member-name-input-${cohort.cohort_id}`}
          className="flex-1 px-3 py-1.5 rounded-lg text-[13px] focus:outline-none" style={inputStyle} />
        <button type="submit" disabled={busy || name.trim().length < 2} data-testid={`member-add-${cohort.cohort_id}`}
          className="px-4 py-1.5 rounded-full text-[12px] font-bold disabled:opacity-40 transition-all hover:-translate-y-0.5" style={btnDark}>
          + Assign & generate ID
        </button>
      </form>
    </div>
  );
}

function FacilityCard({ fac, onChanged, onViewReport }) {
  const [open, setOpen] = useState(false);
  const [cohorts, setCohorts] = useState(null);
  const [cohortName, setCohortName] = useState("");

  const loadCohorts = useCallback(async () => {
    const r = await api.get(`/community/partners/facilities/${fac.facility_id}/cohorts`);
    setCohorts(r.data.cohorts);
  }, [fac.facility_id]);

  useEffect(() => { if (open) loadCohorts(); }, [open, loadCohorts]);

  const addCohort = async (e) => {
    e.preventDefault();
    if (cohortName.trim().length < 2) return;
    try {
      await api.post(`/community/partners/facilities/${fac.facility_id}/cohorts`, { name: cohortName.trim() }, { headers: masterHeaders() });
      setCohortName("");
      loadCohorts();
      onChanged();
    } catch (err) { alert(err?.response?.data?.detail || "Couldn't create cohort."); }
  };
  const delFacility = async () => {
    if (!window.confirm(`Delete facility "${fac.name}" with ALL its cohorts and members? This can't be undone.`)) return;
    await api.delete(`/community/partners/facilities/${fac.facility_id}`, { headers: masterHeaders() });
    onChanged();
  };

  return (
    <div className="rounded-2xl border p-5" style={{ background: C.CARD, borderColor: C.BORDER }} data-testid={`facility-card-${fac.facility_id}`}>
      <div className="flex items-center gap-2.5 flex-wrap">
        <h3 className="text-[18px] font-bold tracking-tight" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{fac.name}</h3>
        <CopyId id={fac.facility_id} testid={`facility-id-${fac.facility_id}`} />
        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: C.PANEL, color: C.ACCENT }}>
          {typeLabel(fac.type)}
        </span>
        <span className="text-[11px]" style={{ color: C.FAINT }}>
          {fac.cohort_count} cohort{fac.cohort_count === 1 ? "" : "s"} · {fac.member_count} member{fac.member_count === 1 ? "" : "s"}
        </span>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={() => onViewReport(fac)} data-testid={`facility-report-${fac.facility_id}`}
            className="px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all hover:-translate-y-0.5" style={btnPrimary}>
            View report
          </button>
          <button type="button" onClick={() => setOpen(!open)} data-testid={`facility-toggle-${fac.facility_id}`}
            className="px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all hover:-translate-y-0.5" style={btnGhost}>
            {open ? "Close" : "Cohorts & people"}
          </button>
          <button type="button" onClick={delFacility} data-testid={`facility-delete-${fac.facility_id}`}
            className="px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all hover:-translate-y-0.5" style={btnDanger}>
            Delete
          </button>
        </div>
      </div>
      {open && (
        <div className="mt-4 space-y-3">
          {cohorts === null && <p className="text-[13px]" style={{ color: C.FAINT }}>Loading…</p>}
          {cohorts?.map((c) => <CohortBlock key={c.cohort_id} cohort={c} onChanged={() => { loadCohorts(); onChanged(); }} />)}
          <form onSubmit={addCohort} className="flex gap-2">
            <input value={cohortName} onChange={(e) => setCohortName(e.target.value)} placeholder="New cohort name (e.g. Fall 2026 Group A)"
              data-testid={`cohort-name-input-${fac.facility_id}`}
              className="flex-1 px-3 py-2 rounded-lg text-[13px] focus:outline-none" style={inputStyle} />
            <button type="submit" disabled={cohortName.trim().length < 2} data-testid={`cohort-add-${fac.facility_id}`}
              className="px-4 py-2 rounded-full text-[12px] font-bold disabled:opacity-40 transition-all hover:-translate-y-0.5" style={btnDark}>
              + Create cohort
            </button>
          </form>
        </div>
      )}
    </div>
  );
}

function ReportView({ report, onClose }) {
  const f = report.facility;
  const e = report.engagement;
  return (
    <div className="rounded-2xl border p-6 mb-8" style={{ background: C.CARD, borderColor: `2px solid ${C.ACCENT}`, border: `2px solid ${C.ACCENT}` }} data-testid="facility-report-view">
      <style>{`
        .fac-print-report { display: none; }
        @media print {
          [data-testid="community-top-bar"], .fac-screen, .no-print,
          [data-testid="community-partner-page"] > section { display: none !important; }
          body { background: #fff !important; }
          .fac-print-report { display: block !important; }
          .print-block { break-inside: avoid; }
        }
      `}</style>
      <div className="fac-screen">
        <div className="flex items-center gap-3 flex-wrap mb-4">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.24em]" style={{ color: C.ACCENT }}>Custom facility report</p>
            <h2 className="text-[24px] font-extrabold tracking-tight" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{f.name}</h2>
            <p className="text-[12px]" style={{ color: C.FAINT }}>{f.facility_id} · {typeLabel(f.type)} · generated {new Date(report.generated_at).toLocaleString()}</p>
          </div>
          <div className="ml-auto flex gap-2">
            <button type="button" onClick={() => window.print()} data-testid="facility-report-print"
              className="px-4 py-2 rounded-full text-[13px] font-bold transition-all hover:-translate-y-0.5" style={btnDark}>
              🖨 Print PDF
            </button>
            <button type="button" onClick={onClose} data-testid="facility-report-close"
              className="px-4 py-2 rounded-full text-[13px] font-bold transition-all hover:-translate-y-0.5" style={btnGhost}>
              Close
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
          {[
            [e.members, "Assigned members"],
            [e.events, "Learning events"],
            [e.events_30d, "Events · last 30 days"],
            [e.cr_events, "Clinical reasoning events"],
          ].map(([v, lbl]) => (
            <div key={lbl} className="rounded-xl border p-4" style={{ background: C.BG, borderColor: C.BORDER }}>
              <p className="text-[28px] font-extrabold leading-none" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{v}</p>
              <p className="text-[12px] font-bold mt-1.5" style={{ color: C.DEEP }}>{lbl}</p>
            </div>
          ))}
        </div>
        {report.cohorts.length === 0
          ? <p className="text-[13px]" style={{ color: C.SUB }}>No cohorts yet — create one and assign people to start collecting engagement.</p>
          : (
            <table className="w-full text-left" data-testid="facility-report-table">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider" style={{ color: C.FAINT }}>
                  <th className="py-2 pr-3">Cohort</th><th className="py-2 pr-3">ID</th><th className="py-2 pr-3">Members</th><th className="py-2">Learning events</th>
                </tr>
              </thead>
              <tbody>
                {report.cohorts.map((c) => (
                  <tr key={c.cohort_id} className="border-t text-[13.5px]" style={{ borderColor: C.BORDER, color: C.SUB }}>
                    <td className="py-2.5 pr-3 font-semibold" style={{ color: C.INK }}>{c.name}</td>
                    <td className="py-2.5 pr-3 font-mono text-[12px]">{c.cohort_id}</td>
                    <td className="py-2.5 pr-3">{c.member_count}</td>
                    <td className="py-2.5">{c.events}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        <p className="text-[11.5px] mt-4 italic" style={{ color: C.FAINT }}>
          Engagement counts events logged under this facility's member IDs. Members use their MEM-ID when signing in so their activity rolls up here — de-identified, cohort-level only.
        </p>
      </div>

      {/* Print-only facility report */}
      <div className="fac-print-report" style={{ color: "#2B2620", fontFamily: C.SANS }}>
        <div style={{ borderBottom: "3px solid #2B2620", paddingBottom: 14, marginBottom: 16 }}>
          <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.22em", textTransform: "uppercase", color: C.ACCENT, margin: 0 }}>Maldek · The Community</p>
          <h1 style={{ fontFamily: C.DISPLAY, fontSize: 28, fontWeight: 800, margin: "6px 0 4px" }}>Facility Analytics Report — {f.name}</h1>
          <p style={{ fontSize: 12, color: C.SUB, margin: 0 }}>{f.facility_id} · {typeLabel(f.type)} · generated {new Date(report.generated_at).toLocaleString()} · live, always-updated figures</p>
        </div>
        <div className="print-block" style={{ display: "flex", gap: 30, marginBottom: 16 }}>
          {[[e.members, "assigned members"], [e.events, "learning events"], [e.events_30d, "events, last 30 days"], [e.cr_events, "clinical reasoning events"]].map(([v, lbl]) => (
            <p key={lbl} style={{ margin: 0, fontSize: 12 }}>
              <span style={{ fontFamily: C.DISPLAY, fontSize: 24, fontWeight: 800, marginRight: 6 }}>{v}</span>
              <span style={{ color: C.SUB }}>{lbl}</span>
            </p>
          ))}
        </div>
        {report.cohorts.map((c) => (
          <div key={c.cohort_id} className="print-block" style={{ border: "1.5px solid #E3D8C2", borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
            <h2 style={{ fontFamily: C.DISPLAY, fontSize: 16, fontWeight: 800, margin: "0 0 2px" }}>{c.name} <span style={{ fontFamily: "monospace", fontSize: 11, fontWeight: 400, color: C.SUB }}>{c.cohort_id}</span></h2>
            <p style={{ fontSize: 12, color: C.SUB, margin: "0 0 6px" }}>{c.member_count} members · {c.events} learning events</p>
            <p style={{ fontSize: 11.5, margin: 0, color: C.SUB }}>{(c.members || []).map((m) => `${m.name} (${m.member_id})`).join(" · ") || "No members assigned yet."}</p>
          </div>
        ))}
        <div className="print-block" style={{ background: "#F3E9D6", borderRadius: 10, padding: "10px 14px" }}>
          <p style={{ fontSize: 11.5, lineHeight: 1.55, margin: 0, color: C.SUB }}>
            <strong style={{ color: "#2B2620" }}>Privacy commitment:</strong> figures are aggregate, de-identified counts under facility-assigned member IDs. No personal health information is collected or shared.
          </p>
        </div>
      </div>
    </div>
  );
}

function InquiriesInbox() {
  const [rows, setRows] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/community/partners/inquiries", { headers: masterHeaders() });
      setRows(r.data.inquiries);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (lead, status) => {
    await api.patch(`/community/partners/inquiries/${lead.lead_id}`, { status }, { headers: masterHeaders() });
    load();
  };
  const del = async (lead) => {
    if (!window.confirm(`Delete inquiry from "${lead.name}" (${lead.organization})? This can't be undone.`)) return;
    await api.delete(`/community/partners/inquiries/${lead.lead_id}`, { headers: masterHeaders() });
    load();
  };

  return (
    <div className="mt-10 no-print" data-testid="partner-inquiries">
      <p className="text-[12px] font-bold uppercase tracking-[0.24em] mb-1" style={{ color: C.ACCENT }}>Facility inquiries</p>
      <h2 className="text-[24px] font-extrabold tracking-tight mb-1" style={{ color: C.INK, fontFamily: C.DISPLAY }}>Contact-form inbox.</h2>
      <p className="text-[13px] mb-5" style={{ color: C.SUB }}>Messages sent from the Facilities & Partners page land here.</p>
      {rows === null && <p className="text-[13px]" style={{ color: C.FAINT }}>Loading…</p>}
      {rows?.length === 0 && <p className="text-[14px]" style={{ color: C.SUB }} data-testid="inquiries-empty">No inquiries yet.</p>}
      <div className="space-y-3">
        {rows?.map((l) => (
          <div key={l.lead_id} className="rounded-2xl border p-5" style={{ background: C.CARD, borderColor: C.BORDER }} data-testid={`inquiry-${l.lead_id}`}>
            <div className="flex items-center gap-2.5 flex-wrap mb-1.5">
              <h3 className="text-[16px] font-bold" style={{ color: C.INK, fontFamily: C.DISPLAY }}>{l.name}</h3>
              <span className="text-[13px] font-semibold" style={{ color: C.DEEP }}>{l.organization}</span>
              {l.program_interest && (
                <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ background: C.PANEL, color: C.ACCENT }}>
                  {l.program_interest}
                </span>
              )}
              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider"
                style={l.status === "contacted"
                  ? { background: "#E9F2E7", color: "#3E7245" }
                  : { background: "#FDF0EC", color: "#A33D2A" }}>
                {l.status}
              </span>
              <span className="text-[11px] ml-auto" style={{ color: C.FAINT }}>{new Date(l.ts).toLocaleString()}</span>
            </div>
            <p className="text-[13px] mb-1" style={{ color: C.SUB }}>
              <a href={`mailto:${l.email}`} className="font-semibold underline" style={{ color: C.ACCENT }} data-testid={`inquiry-email-${l.lead_id}`}>{l.email}</a>
            </p>
            {l.notes && <p className="text-[13.5px] leading-relaxed mb-3" style={{ color: C.SUB }}>{l.notes}</p>}
            <div className="flex gap-2">
              {l.status !== "contacted" ? (
                <button type="button" onClick={() => setStatus(l, "contacted")} data-testid={`inquiry-contacted-${l.lead_id}`}
                  className="px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all hover:-translate-y-0.5" style={btnDark}>
                  Mark contacted
                </button>
              ) : (
                <button type="button" onClick={() => setStatus(l, "new")} data-testid={`inquiry-reopen-${l.lead_id}`}
                  className="px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all hover:-translate-y-0.5" style={btnGhost}>
                  Mark as new
                </button>
              )}
              <button type="button" onClick={() => del(l)} data-testid={`inquiry-delete-${l.lead_id}`}
                className="px-3.5 py-1.5 rounded-full text-[12px] font-bold transition-all hover:-translate-y-0.5" style={btnDanger}>
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function CommunityPartnerReports() {
  const [unlocked, setUnlocked] = useState(() => !!localStorage.getItem(LS_CODE));
  const [facilities, setFacilities] = useState(null);
  const [name, setName] = useState("");
  const [type, setType] = useState("doctors_office");
  const [creating, setCreating] = useState(false);
  const [report, setReport] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/community/partners/facilities");
    setFacilities(r.data.facilities);
  }, []);

  useEffect(() => { if (unlocked) load(); }, [unlocked, load]);

  const createFacility = async (e) => {
    e.preventDefault();
    if (name.trim().length < 2) return;
    setCreating(true);
    try {
      await api.post("/community/partners/facilities", { name: name.trim(), type }, { headers: masterHeaders() });
      setName("");
      load();
    } catch (err) {
      if (err?.response?.status === 403) { localStorage.removeItem(LS_CODE); setUnlocked(false); }
      else alert(err?.response?.data?.detail || "Couldn't create facility.");
    } finally { setCreating(false); }
  };

  const viewReport = async (fac) => {
    const r = await api.get(`/community/partners/facilities/${fac.facility_id}/report`);
    setReport(r.data);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <CommunityShell
      testid="community-partner-page"
      eyebrow="Custom facility reports"
      title="Partner reporting workspace."
      intro="Create facilities, generate custom IDs, assign people to cohorts, and pull facility-specific analytics reports — with full control to delete anything made in error."
    >
      <div className="flex flex-wrap gap-3 mb-8 no-print" data-testid="partner-actions">
        <Link to="/community/analytics" data-testid="partner-view-platform-report"
          className="px-5 py-2.5 rounded-full text-[13px] font-bold transition-all hover:-translate-y-0.5" style={btnDark}>
          ← View platform analytics report
        </Link>
        {unlocked && (
          <button type="button" data-testid="partner-lock"
            onClick={() => { localStorage.removeItem(LS_CODE); setUnlocked(false); }}
            className="px-5 py-2.5 rounded-full text-[13px] font-bold transition-all hover:-translate-y-0.5" style={btnGhost}>
            Lock workspace
          </button>
        )}
      </div>

      {!unlocked ? <Gate onUnlocked={() => setUnlocked(true)} /> : (
        <>
          {report && <ReportView report={report} onClose={() => setReport(null)} />}

          {/* Create facility */}
          <form onSubmit={createFacility} className="rounded-2xl border p-6 mb-8 no-print" style={{ background: C.CARD, borderColor: C.BORDER }} data-testid="facility-create-form">
            <p className="text-[12px] font-bold uppercase tracking-[0.24em] mb-3" style={{ color: C.ACCENT }}>New facility</p>
            <div className="flex flex-col sm:flex-row gap-2">
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Facility name (e.g. Riverside Family Clinic)"
                data-testid="facility-name-input"
                className="flex-1 px-4 py-2.5 rounded-xl text-[14px] focus:outline-none" style={inputStyle} />
              <select value={type} onChange={(e) => setType(e.target.value)} data-testid="facility-type-select"
                className="px-3 py-2.5 rounded-xl text-[13px] focus:outline-none" style={inputStyle}>
                {TYPES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
              <button type="submit" disabled={creating || name.trim().length < 2} data-testid="facility-create-btn"
                className="px-6 py-2.5 rounded-full text-[13px] font-bold disabled:opacity-40 transition-all hover:-translate-y-0.5" style={btnPrimary}>
                {creating ? "Creating…" : "+ Create facility"}
              </button>
            </div>
          </form>

          {/* Facilities */}
          <div className="space-y-4 no-print" data-testid="facility-list">
            {facilities === null && <p className="text-[13px]" style={{ color: C.FAINT }}>Loading…</p>}
            {facilities?.length === 0 && (
              <p className="text-[14px]" style={{ color: C.SUB }} data-testid="facility-empty">
                No facilities yet — create your first one above.
              </p>
            )}
            {facilities?.map((f) => (
              <FacilityCard key={f.facility_id} fac={f} onChanged={load} onViewReport={viewReport} />
            ))}
          </div>

          <InquiriesInbox />
        </>
      )}
    </CommunityShell>
  );
}
